// Ubicación en el repo: src/components/ExamFormModal.jsx
// Modal Crear/Editar audiometría — Fase 1 (ver DECISIONS.md): paciente,
// fecha, profesional (filtrado por rol ORL + sucursal, igual que en el
// modal de cita), equipo, notas. Los campos clínicos (vía aérea/ósea/LDL,
// diapasones, logoaudiometría) se agregan en fases siguientes sobre este
// mismo componente.
import { useState } from 'react';
import {
  createExam,
  updateExam,
  FREQS,
  BONE_FREQS,
  LDL_FREQS,
  freqValuesToInputs,
  freqInputsToValues,
  freqMaskValuesToInputs,
  freqMaskInputsToValues,
  invalidFreqEntries,
  calcPTA,
  ptaLabel
} from '../lib/exams';
import { searchPatients } from '../lib/patients';
import { professionalsForModuleAndBranch } from '../lib/professionals';
import { formatPatientName } from '../lib/format';
import FrequencyTable from './FrequencyTable';

export const LOGO_TEST_TYPES = ['Monosílabos', 'Bisílabos', 'Términos conocidos'];

function emptyLogo() {
  return { percent: '', db: '', masking: '' };
}
function logoToInputs(logo) {
  if (!logo) return emptyLogo();
  return {
    percent: logo.percent ?? '',
    db: logo.db ?? '',
    masking: logo.masking ?? '',
  };
}
function logoToValues(logo) {
  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  return { percent: num(logo.percent), db: num(logo.db), masking: num(logo.masking) };
}

function initialForm(exam) {
  if (exam) {
    return {
      patientId: exam.patient_id,
      patientLabel: exam.patients ? formatPatientName(exam.patients) : '',
      date: exam.date,
      professionalId: exam.professional_id || '',
      equipment: exam.equipment || '',
      notes: exam.notes || '',
      od: freqValuesToInputs(exam.od),
      oi: freqValuesToInputs(exam.oi),
      boneOd: freqValuesToInputs(exam.bone_od),
      boneOi: freqValuesToInputs(exam.bone_oi),
      ldlOd: freqValuesToInputs(exam.ldl_od),
      ldlOi: freqValuesToInputs(exam.ldl_oi),
      // Enmascaramiento por frecuencia (migración 0053, 16-sep-2026) —
      // columnas nuevas, independientes de od/oi/bone_od/bone_oi.
      odMasked: freqMaskValuesToInputs(exam.od_masked),
      oiMasked: freqMaskValuesToInputs(exam.oi_masked),
      boneOdMasked: freqMaskValuesToInputs(exam.bone_od_masked),
      boneOiMasked: freqMaskValuesToInputs(exam.bone_oi_masked),
      // Sin respuesta / sin umbral (migración 0056, 15-oct-2026) — mismo
      // formato que los mapas de enmascaramiento.
      odNr: freqMaskValuesToInputs(exam.od_nr),
      oiNr: freqMaskValuesToInputs(exam.oi_nr),
      boneOdNr: freqMaskValuesToInputs(exam.bone_od_nr),
      boneOiNr: freqMaskValuesToInputs(exam.bone_oi_nr),
      // Modo del promedio de umbral (migración 0057): '4' o '3' frecuencias.
      ptaMode: String(exam.pta_mode ?? 4),
      rinne: exam.rinne || {},
      weber: exam.weber || {},
      logoTestType: exam.logo_test_type || '',
      logoOd: logoToInputs(exam.logo_od),
      logoOi: logoToInputs(exam.logo_oi),
    };
  }
  return {
    patientId: null,
    patientLabel: '',
    date: new Date().toLocaleDateString('sv-SE'), // YYYY-MM-DD en hora local, no UTC
    professionalId: '',
    equipment: 'Interacoustics AD629',
    notes: '',
    od: {},
    oi: {},
    boneOd: {},
    boneOi: {},
    ldlOd: {},
    ldlOi: {},
    odMasked: {},
    oiMasked: {},
    boneOdMasked: {},
    boneOiMasked: {},
    odNr: {},
    oiNr: {},
    boneOdNr: {},
    boneOiNr: {},
    ptaMode: '4',
    rinne: {},
    weber: {},
    logoTestType: '',
    logoOd: emptyLogo(),
    logoOi: emptyLogo(),
  };
}

export default function ExamFormModal({ exam, branchId, professionals, actorId, onClose, onSaved }) {
  const isEdit = !!exam;
  const [form, setForm] = useState(() => initialForm(exam));
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // PTP (promedio tonal puro) en vivo, mientras se cargan los umbrales —
  // calcPTA acepta directamente los objetos de inputs (string) porque
  // internamente hace Number(v) sobre lo que sí tiene valor. No es
  // editable: es puro cálculo derivado del estado, igual que en
  // ExamDetailModal.jsx/examReport.jsx (ver DECISIONS.md #47/#48).
  const ptaAirOd = calcPTA(form.od, form.ptaMode, form.odNr);
  const ptaAirOi = calcPTA(form.oi, form.ptaMode, form.oiNr);
  const ptaBoneOd = calcPTA(form.boneOd, form.ptaMode, form.boneOdNr);
  const ptaBoneOi = calcPTA(form.boneOi, form.ptaMode, form.boneOiNr);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  // group = 'od' | 'oi' | 'boneOd' | 'boneOi' | 'ldlOd' | 'ldlOi'
  function setFreq(odGroup, oiGroup) {
    return (ear, freq, value) => {
      const group = ear === 'od' ? odGroup : oiGroup;
      setForm((f) => ({ ...f, [group]: { ...f[group], [freq]: value } }));
    };
  }

  // Enmascaramiento por frecuencia (migración 0053) — mismo patrón que
  // setFreq de arriba, pero el value es boolean y el grupo destino siempre
  // termina en "Masked". Se quita la clave del objeto al destildar (no se
  // guarda `false`) para que solo queden presentes las frecuencias
  // realmente marcadas, igual que espera freqMaskInputsToValues().
  function setMask(odGroup, oiGroup) {
    return (ear, freq, checked) => {
      const group = ear === 'od' ? odGroup : oiGroup;
      setForm((f) => {
        const next = { ...f[group] };
        if (checked) next[freq] = true;
        else delete next[freq];
        return { ...f, [group]: next };
      });
    };
  }

  // test = 'rinne' | 'weber', key = 'od250' | 'oi250' | 'od500' | 'oi500'
  function setTuningFork(test, key, value) {
    setForm((f) => ({ ...f, [test]: { ...f[test], [key]: value } }));
  }

  // Copia los umbrales de vía aérea a vía ósea, solo en las frecuencias
  // que vía ósea realmente usa (BONE_FREQS es un subconjunto de FREQS —
  // no tiene 125/6000/8000 Hz). Si ya hay algo cargado en vía ósea,
  // confirma antes de pisarlo (puede ser una edición, no siempre un
  // examen nuevo).
  function copyAirToBone() {
    const hasBoneData = BONE_FREQS.some((f) => form.boneOd[f] || form.boneOi[f]);
    if (hasBoneData && !window.confirm('Ya hay valores cargados en vía ósea. ¿Reemplazarlos con los de vía aérea?')) {
      return;
    }
    setForm((f) => {
      const boneOd = { ...f.boneOd };
      const boneOi = { ...f.boneOi };
      // El enmascaramiento también se copia — si un umbral de vía aérea
      // estaba marcado como enmascarado, tiene sentido que su copia en vía
      // ósea arranque igual (la persona la sigue ajustando desde ahí).
      const boneOdMasked = { ...f.boneOdMasked };
      const boneOiMasked = { ...f.boneOiMasked };
      const boneOdNr = { ...f.boneOdNr };
      const boneOiNr = { ...f.boneOiNr };
      BONE_FREQS.forEach((freq) => {
        if (f.odNr[freq]) boneOdNr[freq] = true;
        else delete boneOdNr[freq];
        if (f.oiNr[freq]) boneOiNr[freq] = true;
        else delete boneOiNr[freq];
        if (f.od[freq] !== undefined && f.od[freq] !== '') boneOd[freq] = f.od[freq];
        if (f.oi[freq] !== undefined && f.oi[freq] !== '') boneOi[freq] = f.oi[freq];
        if (f.odMasked[freq]) boneOdMasked[freq] = true;
        else delete boneOdMasked[freq];
        if (f.oiMasked[freq]) boneOiMasked[freq] = true;
        else delete boneOiMasked[freq];
      });
      return { ...f, boneOd, boneOi, boneOdMasked, boneOiMasked, boneOdNr, boneOiNr };
    });
  }

  function setLogo(ear, field, value) {
    const group = ear === 'od' ? 'logoOd' : 'logoOi';
    setForm((f) => ({ ...f, [group]: { ...f[group], [field]: value } }));
  }

  function selectPatient(p) {
    set('patientId', p.id);
    setForm((f) => ({ ...f, patientLabel: formatPatientName(p) }));
    setPatientQuery('');
    setPatientResults([]);
  }

  async function handlePatientSearch(value) {
    setPatientQuery(value);
    if (!value.trim()) {
      setPatientResults([]);
      return;
    }
    try {
      const results = await searchPatients(value);
      setPatientResults(results);
    } catch {
      setPatientResults([]);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.patientId) {
      setError('Selecciona un paciente.');
      return;
    }
    if (!form.date) {
      setError('Indica la fecha del examen.');
      return;
    }
    // Rango 0-120 dB, de 5 en 5 (migración 0053, 16-sep-2026, pedido de
    // Nicolás) — min/max/step en el input ya ayudan con las flechitas, pero
    // no bloquean un valor tipeado a mano o pegado; este chequeo sí lo hace,
    // con un mensaje claro de qué frecuencia(s) están fuera de rango. La
    // base de datos tiene el mismo límite como garantía real (ver
    // migración), esto es solo para no hacerle descubrir el error recién al
    // guardar contra el servidor.
    const badEntries = [
      ...invalidFreqEntries(form.od),
      ...invalidFreqEntries(form.oi),
      ...invalidFreqEntries(form.boneOd),
      ...invalidFreqEntries(form.boneOi),
      ...invalidFreqEntries(form.ldlOd),
      ...invalidFreqEntries(form.ldlOi),
    ];
    if (badEntries.length > 0) {
      setError(`Los umbrales van de 0 a 120 dB, de 5 en 5. Revisa: ${badEntries.join(', ')}.`);
      return;
    }
    // Sin respuesta (migración 0056): el símbolo se dibuja en el dB cargado
    // (nivel máximo evaluado) — sin dB no hay dónde ubicarlo en el
    // audiograma, así que se exige antes de guardar.
    const nrSinDb = [];
    [
      ['vía aérea OD', form.odNr, form.od],
      ['vía aérea OI', form.oiNr, form.oi],
      ['vía ósea OD', form.boneOdNr, form.boneOd],
      ['vía ósea OI', form.boneOiNr, form.boneOi],
    ].forEach(([label, nrMap, dbMap]) => {
      Object.keys(nrMap).forEach((freq) => {
        if (nrMap[freq] && (dbMap[freq] === undefined || dbMap[freq] === '')) nrSinDb.push(`${label} ${freq} Hz`);
      });
    });
    if (nrSinDb.length > 0) {
      setError(`Para marcar "SR" indica el nivel máximo evaluado en dB. Falta: ${nrSinDb.join(', ')}.`);
      return;
    }
    setSaving(true);
    try {
      const values = {
        patient_id: form.patientId,
        date: form.date,
        professional_id: form.professionalId || null,
        equipment: form.equipment.trim() || null,
        notes: form.notes.trim() || null,
        od: freqInputsToValues(form.od),
        oi: freqInputsToValues(form.oi),
        bone_od: freqInputsToValues(form.boneOd),
        bone_oi: freqInputsToValues(form.boneOi),
        ldl_od: freqInputsToValues(form.ldlOd),
        ldl_oi: freqInputsToValues(form.ldlOi),
        od_masked: freqMaskInputsToValues(form.odMasked),
        oi_masked: freqMaskInputsToValues(form.oiMasked),
        bone_od_masked: freqMaskInputsToValues(form.boneOdMasked),
        bone_oi_masked: freqMaskInputsToValues(form.boneOiMasked),
        pta_mode: Number(form.ptaMode) === 3 ? 3 : 4,
        od_nr: freqMaskInputsToValues(form.odNr),
        oi_nr: freqMaskInputsToValues(form.oiNr),
        bone_od_nr: freqMaskInputsToValues(form.boneOdNr),
        bone_oi_nr: freqMaskInputsToValues(form.boneOiNr),
        rinne: form.rinne,
        weber: form.weber,
        logo_test_type: form.logoTestType || null,
        logo_od: logoToValues(form.logoOd),
        logo_oi: logoToValues(form.logoOi),
      };
      if (isEdit) {
        await updateExam(exam.id, values, actorId);
      } else {
        await createExam(values, actorId);
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'No se pudo guardar la audiometría.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card modal-card--wide">
        <h2>{isEdit ? 'Editar audiometría' : 'Nueva audiometría'}</h2>

        <form onSubmit={handleSubmit} className="patient-form">
          <div className="field">
            <span>Paciente *</span>
            {form.patientId ? (
              <div className="patient-picker-selected">
                {form.patientLabel}
                {!isEdit && (
                  <button type="button" className="link-button" onClick={() => set('patientId', null)}>
                    Cambiar
                  </button>
                )}
              </div>
            ) : (
              <>
                <input
                  value={patientQuery}
                  onChange={(e) => handlePatientSearch(e.target.value)}
                  placeholder="Buscar por nombre o RUT…"
                />
                {patientResults.length > 0 && (
                  <ul className="patient-picker-results">
                    {patientResults.map((p) => (
                      <li key={p.id}>
                        <button type="button" onClick={() => selectPatient(p)}>
                          {formatPatientName(p)} {p.rut ? `— ${p.rut}` : ''}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          <div className="form-row">
            <label className="field">
              <span>Fecha del examen *</span>
              <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required />
            </label>
            <label className="field">
              <span>Profesional que realiza el examen</span>
              <select value={form.professionalId} onChange={(e) => set('professionalId', e.target.value)}>
                <option value="">Sin asignar</option>
                {professionalsForModuleAndBranch(professionals, 'ORL', branchId).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre} {p.apellido} ({p.role})
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="field">
            <span>Promedio de umbral (PTP) — frecuencias a promediar</span>
            <select value={form.ptaMode} onChange={(e) => set('ptaMode', e.target.value)}>
              <option value="4">4 frecuencias (500, 1000, 2000 y 4000 Hz)</option>
              <option value="3">3 frecuencias (500, 1000 y 2000 Hz)</option>
            </select>
            <span className="field-hint">Aplica a vía aérea y vía ósea, en el detalle y en el informe impreso.</span>
          </label>

          <div className="field">
            <span>Vía aérea — umbrales por frecuencia (dB HL)</span>
            <FrequencyTable
              label="vía aérea"
              freqs={FREQS}
              odValues={form.od}
              oiValues={form.oi}
              onChange={setFreq('od', 'oi')}
              odMasked={form.odMasked}
              oiMasked={form.oiMasked}
              onToggleMask={setMask('odMasked', 'oiMasked')}
              odNr={form.odNr}
              oiNr={form.oiNr}
              onToggleNr={setMask('odNr', 'oiNr')}
            />
            <p className="field-hint" style={{ marginTop: 2 }}>
              "E" = enmascarado (se usó ruido en el oído contrario al marcado). Aparece con símbolo distinto
              en el audiograma. "SR" = sin respuesta (sin umbral): carga en dB el nivel máximo evaluado y el
              símbolo se dibuja ahí con una flecha hacia abajo (se puede combinar con "E").
            </p>
            <div className="field-hint" style={{ display: 'flex', gap: 24, marginTop: 6 }}>
              <span>
                PTP OD:{' '}
                <strong className="freq-table__od">{ptaAirOd !== null ? `${ptaAirOd} dB` : 'No calculable'}</strong>
              </span>
              <span>
                PTP OI:{' '}
                <strong className="freq-table__oi">{ptaAirOi !== null ? `${ptaAirOi} dB` : 'No calculable'}</strong>
              </span>
            </div>
          </div>

          <div className="field">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span>Vía ósea (opcional, 250 a 4000 Hz)</span>
              <button type="button" className="button-secondary" onClick={copyAirToBone}>
                Copiar de vía aérea
              </button>
            </div>
            <FrequencyTable
              label="vía ósea"
              freqs={BONE_FREQS}
              odValues={form.boneOd}
              oiValues={form.boneOi}
              onChange={setFreq('boneOd', 'boneOi')}
              odMasked={form.boneOdMasked}
              oiMasked={form.boneOiMasked}
              onToggleMask={setMask('boneOdMasked', 'boneOiMasked')}
              odNr={form.boneOdNr}
              oiNr={form.boneOiNr}
              onToggleNr={setMask('boneOdNr', 'boneOiNr')}
            />
            <div className="field-hint" style={{ display: 'flex', gap: 24, marginTop: 6 }}>
              <span>
                PTP OD:{' '}
                <strong className="freq-table__od">{ptaBoneOd !== null ? `${ptaBoneOd} dB` : 'No calculable'}</strong>
              </span>
              <span>
                PTP OI:{' '}
                <strong className="freq-table__oi">{ptaBoneOi !== null ? `${ptaBoneOi} dB` : 'No calculable'}</strong>
              </span>
            </div>
          </div>

          <div className="field">
            <span>Umbral de disconfort · LDL (opcional, 250 a 4000 Hz)</span>
            <FrequencyTable
              label="LDL"
              freqs={LDL_FREQS}
              odValues={form.ldlOd}
              oiValues={form.ldlOi}
              onChange={setFreq('ldlOd', 'ldlOi')}
            />
          </div>

          <div className="field">
            <span>Diapasones</span>
            <div className="form-row">
              <div>
                <div className="field-hint" style={{ fontWeight: 700, marginBottom: 4 }}>
                  Rinne
                </div>
                <table className="freq-table">
                  <thead>
                    <tr>
                      <th>Frec.</th>
                      <th className="freq-table__od">OD</th>
                      <th className="freq-table__oi">OI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[250, 500].map((freq) => (
                      <tr key={freq}>
                        <td>{freq} Hz</td>
                        <td>
                          <input
                            type="text"
                            value={form.rinne[`od${freq}`] || ''}
                            onChange={(e) => setTuningFork('rinne', `od${freq}`, e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={form.rinne[`oi${freq}`] || ''}
                            onChange={(e) => setTuningFork('rinne', `oi${freq}`, e.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <div className="field-hint" style={{ fontWeight: 700, marginBottom: 4 }}>
                  Weber
                </div>
                <table className="freq-table">
                  <thead>
                    <tr>
                      <th>Frec.</th>
                      <th className="freq-table__od">OD</th>
                      <th className="freq-table__oi">OI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[250, 500].map((freq) => (
                      <tr key={freq}>
                        <td>{freq} Hz</td>
                        <td>
                          <input
                            type="text"
                            value={form.weber[`od${freq}`] || ''}
                            onChange={(e) => setTuningFork('weber', `od${freq}`, e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={form.weber[`oi${freq}`] || ''}
                            onChange={(e) => setTuningFork('weber', `oi${freq}`, e.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="field">
            <span>Logoaudiometría (opcional)</span>
            <p className="field-hint">Lista de palabras utilizada:</p>
            <div className="checkbox-group">
              {LOGO_TEST_TYPES.map((t) => (
                <label key={t} className="field--checkbox">
                  <input
                    type="radio"
                    name="logoTestType"
                    checked={form.logoTestType === t}
                    onChange={() => set('logoTestType', t)}
                  />
                  {t}
                </label>
              ))}
            </div>
            <table className="freq-table" style={{ maxWidth: 420 }}>
              <thead>
                <tr>
                  <th>Discriminación</th>
                  <th>%</th>
                  <th>dB</th>
                  <th>MKG</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="freq-table__od" style={{ fontWeight: 700 }}>
                    OD
                  </td>
                  <td>
                    <input
                      type="number"
                      value={form.logoOd.percent}
                      onChange={(e) => setLogo('od', 'percent', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={form.logoOd.db}
                      onChange={(e) => setLogo('od', 'db', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      title="Enmascaramiento contralateral (OI)"
                      value={form.logoOd.masking}
                      onChange={(e) => setLogo('od', 'masking', e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="freq-table__oi" style={{ fontWeight: 700 }}>
                    OI
                  </td>
                  <td>
                    <input
                      type="number"
                      value={form.logoOi.percent}
                      onChange={(e) => setLogo('oi', 'percent', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={form.logoOi.db}
                      onChange={(e) => setLogo('oi', 'db', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      title="Enmascaramiento contralateral (OD)"
                      value={form.logoOi.masking}
                      onChange={(e) => setLogo('oi', 'masking', e.target.value)}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="field-hint">
              El MKG se muestra en el color del oído contralateral (el que recibe el enmascaramiento). El % y
              dB de esta tabla son los que se grafican automáticamente.
            </p>
          </div>

          <label className="field">
            <span>Equipo utilizado</span>
            <input
              type="text"
              value={form.equipment}
              onChange={(e) => set('equipment', e.target.value)}
              placeholder="Ej: Interacoustics AD629"
            />
          </label>

          <label className="field">
            <span>Observaciones / interpretación</span>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Tipo y grado de hipoacusia, recomendación…"
            />
          </label>

          {error && <p className="auth-error">{error}</p>}

          <div className="modal-actions">
            <button type="button" className="button-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="button-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
